import React, { useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { colors } from '@/globalStyles';
import { MATRIX_CELL_WIDTH } from '@/constants';
import UsefullNumericSizeInput from '@/components/NumericInput/UsefullNumericSizeInput';
import UseMatrixRowInput from '@/components/NumericInput/UseMatrixRowInput';
import InlineButtonsGroup from '@/components/InlineButtonsGroup';
import { GridContainer } from '@/components/SideBar/styles';
import { Label } from '@/components';
import IconCircleMinusDisabled from '@/icons/icon-circle-minus-disabled.svg';
import IconCircleMinusRed from '@/icons/icon-circle-minus-red.svg';
import IconCirclePlus from '@/icons/icon-circle-plus.inline.svg';
import IconMinus from '@/icons/icon-cmme-minus-grey.svg';
import IconPlus from '@/icons/icon-cmme-plus-grey.svg';
import IconUseLabelSize from '../../Icon/CmmeIcons/IconUseLabelSize';
import IconUseFixedCellWidth from '../../Icon/CmmeIcons/IconUseFixedCellWidth';
import IconMatrixSeparator from '../../Icon/CmmeIcons/IconMatrixSeparator';
import {
  MatrixFormatWrapper,
  MatrixRemoveCell,
  MatrixCellRect,
  VerticalLine,
  InputContainerWhite,
  LabelBold,
  Icon20,
  AddRowStyled,
} from './styles';

const MatrixFormatForm = props => {
  const {
    isEndlessMaterial,
    isMatrixFixedCellWidth,
    isMatrixSeparator,
    matrixRows = [],
    matrixCellWidth,
    matrixSeparatorWidth,
    userSizeUnit,
    onChange
  } = props;
  const intl = useIntl();
  const inputStyles = { borderRadius: '4px', backgroundColor: colors.white };  
  const setIsMatrixFixedCellWidth = useCallback(newVal => onChange({ isMatrixFixedCellWidth: newVal }), [onChange]);
  const setIsMatrixSeparator = useCallback(newVal => onChange({ isMatrixSeparator: newVal }), [onChange]);
  const onMatrixCellWidthChangeClb = useCallback(newVal => onChange({ matrixCellWidth: newVal }), [onChange]);
  const onMatrixSeparatorWidthChangeClb = useCallback(newVal => onChange({ matrixSeparatorWidth: newVal }), [onChange]);

  const removeMatrixRow = index => {
    if (matrixRows.length > 0) {
      onChange({
        matrixRows: matrixRows.filter((_, rowNumber) => index !== rowNumber)
      });
    }
  };

  const addMatrixRow = () => {
    onChange({
      matrixRows: [...matrixRows, matrixRows[matrixRows.length - 1]]
    });
  };

  const handleCellsChange = payload => {
    const { data, index, changed } = payload;
    const rows = [...matrixRows];
    const cellsNumber = parseInt(data, 10) || 1;
    const cells = new Array(cellsNumber).fill(null).map(() => ({
      type: 'cell',
      width: matrixCellWidth,
      children: [],
    }));

    rows[index] = { cells };

    onChange({ matrixRows: rows });
  };

  return (
    <MatrixFormatWrapper>
      {isEndlessMaterial && (
        <>
          <GridContainer gridTemplateColumns="1fr">
            <LabelBold>
              <FormattedMessage id="cmme.cellWidth" defaultMessage="Cell width" />
            </LabelBold>
          </GridContainer>

          <GridContainer gridTemplateColumns="1fr 1fr">
            <Label fontFamily="Lato" fontSize={14}>
              <FormattedMessage
                id="app.editor.sideMenu.panelLabel.matrixUseLabelLength"
                defaultMessage="Use size of the Label"
              />
            </Label>
            <div></div>
            <InputContainerWhite>
              <InlineButtonsGroup
                backgroundColor="transparent"
                items={[
                  {
                    icon: <IconUseLabelSize fill={!isMatrixFixedCellWidth ? '#007e83' : '#202020'} />,
                    dataValue: 'use-label-size',
                    selected: !isMatrixFixedCellWidth,
                    onClick: () => setIsMatrixFixedCellWidth(false),
                  },
                ]}
              />
            </InputContainerWhite>
            <div></div>
            <Label fontFamily="Lato" fontSize={14}>
              <FormattedMessage id="app.editor.sideMenu.panelLabel.matrixCellWidth" defaultMessage="Define cell width" />
              <span></span>
              (<FormattedMessage id={`app.sizeUnits.${userSizeUnit}`} defaultMessage={userSizeUnit} />)
            </Label>
            <div></div>
            <InputContainerWhite>
              <InlineButtonsGroup
                backgroundColor="transparent"
                items={[
                  {
                    icon: <IconUseFixedCellWidth fill={isMatrixFixedCellWidth ? '#007e83' : '#202020'} />,
                    dataValue: 'define-cell-width',
                    selected: isMatrixFixedCellWidth,
                    onClick: () => setIsMatrixFixedCellWidth(true),
                  },
                ]}
              />
            </InputContainerWhite>
            {isMatrixFixedCellWidth ? (
              <UsefullNumericSizeInput
                dataValue="define-cell-width"
                className="inputContainer"
                onChange={onMatrixCellWidthChangeClb}
                value={matrixCellWidth ? matrixCellWidth : MATRIX_CELL_WIDTH}
                units={userSizeUnit}
                iconColor={colors.whisperGray}
                hoverColor={colors.whiteSmoke}
                selectedColor={colors.primary}
                iconMinus={IconMinus}
                iconPlus={IconPlus}
                disabled={!isMatrixFixedCellWidth}
                stepMm={0.1}
                minval={0.1}
                {...inputStyles}
              />
            ) : (
              ''
            )}
          </GridContainer>
        </>
      )}
      <GridContainer gridTemplateColumns="1fr 1fr">
        <LabelBold>
          <FormattedMessage id="app.editor.sideMenu.panelLabel.matrixLines" defaultMessage="Lines:" />
        </LabelBold>
        <LabelBold>
          <FormattedMessage id="app.editor.sideMenu.panelLabel.matrixCells" defaultMessage="Cells:" />
        </LabelBold>
      </GridContainer>
      {matrixRows.map((mtxRow, index, arr) => {
        const isNotLastRow = arr.length > 1;

        return (
          <GridContainer key={'mtx' + index} gridTemplateColumns="1fr 1fr">
            <MatrixRemoveCell>
              <Icon20
                src={isNotLastRow ? IconCircleMinusRed : IconCircleMinusDisabled}
                data-value={`remove-matrix-row-${index + 1}`}
                onClick={() => (isNotLastRow ? removeMatrixRow(index) : null)}
              />
              <MatrixCellRect>
                {mtxRow.cells.map((cell, idx) => {
                  return idx > 0 ? <VerticalLine key={'mtx' + index + '_' + idx} /> : '';
                })}
              </MatrixCellRect>
            </MatrixRemoveCell>

            <UseMatrixRowInput
              dataValue={`matrix-row-${index + 1}-cells`}
              onChange={handleCellsChange}
              className="inputContainer"
              hoverColor={colors.whiteSmoke}
              selectedColor={colors.primary}
              iconMinus={IconMinus}
              iconPlus={IconPlus}
              index={index}
              value={mtxRow.cells.length}
              stepMm={1}
              minval={1}
              {...inputStyles}
            />
          </GridContainer>
        );
      })}
      <GridContainer gridTemplateColumns="1fr">
        <AddRowStyled
          data-value="add-matrix-row"
          onClick={addMatrixRow}
        >
          <IconCirclePlus/>
          <Label fontFamily="Lato" fontSize={14}>
            <FormattedMessage
              id="app.editor.sideMenu.panelLabel.matrixAddLine"
              defaultMessage="Add line"
            />
          </Label>
        </AddRowStyled>
      </GridContainer>
      <GridContainer gridTemplateColumns="1fr 1fr">
        <Label fontFamily="Lato" fontSize={14}>
          {`${intl.formatMessage({
            id: 'app.editor.sideMenu.panelLabel.separator',
            defaultMessage: 'Separator',
          })} (${intl.formatMessage({
            id: `app.sizeUnits.${userSizeUnit}`,
            defaultMessage: userSizeUnit,
          })})`}
        </Label>
        <div></div>
        <InputContainerWhite>
          <InlineButtonsGroup
            backgroundColor="transparent"
            items={[
              {
                icon: <IconMatrixSeparator fill={isMatrixSeparator ? '#007e83' : '#202020'} />,
                dataValue: 'matrix-separator',
                selected: isMatrixSeparator,
                onClick: () => setIsMatrixSeparator(!isMatrixSeparator),
              },
            ]}
          />
        </InputContainerWhite>
        {isMatrixSeparator ? (
          <UsefullNumericSizeInput
            dataValue="matrix-separator"
            className="inputContainer"
            onChange={onMatrixSeparatorWidthChangeClb}
            value={matrixSeparatorWidth ? matrixSeparatorWidth : 0.5}
            disabled={!isMatrixSeparator}
            units={userSizeUnit}
            iconColor={colors.whisperGray}
            hoverColor={colors.whiteSmoke}
            selectedColor={colors.primary}
            iconMinus={IconMinus}
            iconPlus={IconPlus}
            stepMm={0.1}
            minval={0.1}
            {...inputStyles}
          />
        ) : null}
        <div></div>
      </GridContainer>
    </MatrixFormatWrapper>
  );
};

export default React.memo(MatrixFormatForm);
